import React, { useState, useEffect } from 'react';
import { 
  getAllRoomTypes, 
  createRoomType, 
  updateRoomType, 
  deleteRoomType 
} from '../../api';

const RoomTypeManagement = () => {
  const [roomTypes, setRoomTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingRoomType, setEditingRoomType] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [notification, setNotification] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    max_capacity: '',
    price_per_night: ''
  });

  useEffect(() => {
    fetchRoomTypes();
  }, []);

  // Auto-dismiss notification after 3 seconds
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        setNotification(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
  };

  const fetchRoomTypes = async () => {
    try {
      const data = await getAllRoomTypes();
      setRoomTypes(data);
    } catch (error) {
      console.error('Error fetching room types:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const roomTypeData = {
        ...formData,
        max_capacity: parseInt(formData.max_capacity),
        price_per_night: parseFloat(formData.price_per_night)
      };

      if (editingRoomType) {
        await updateRoomType(editingRoomType.id, roomTypeData);
        showNotification('Room type updated successfully!', 'success');
      } else {
        await createRoomType(roomTypeData);
        showNotification('Room type created successfully!', 'success');
      }
      
      fetchRoomTypes();
      resetForm();
    } catch (error) {
      console.error('Error saving room type:', error);
      showNotification('Error saving room type. Please try again.', 'error');
    }
  };

  const handleEdit = (roomType) => {
    setEditingRoomType(roomType);
    setFormData({
      name: roomType.name,
      description: roomType.description,
      max_capacity: roomType.max_capacity.toString(),
      price_per_night: roomType.price_per_night.toString()
    });
    setShowForm(true);
  };

  const handleDelete = (roomType) => {
    setItemToDelete(roomType);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    try {
      await deleteRoomType(itemToDelete.id);
      fetchRoomTypes();
      showNotification('Room type deleted successfully!', 'success');
      setShowDeleteDialog(false);
      setItemToDelete(null);
    } catch (error) {
      console.error('Error deleting room type:', error);
      showNotification('Error deleting room type. Please try again.', 'error');
    }
  };

  const cancelDelete = () => {
    setShowDeleteDialog(false);
    setItemToDelete(null);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      max_capacity: '',
      price_per_night: ''
    });
    setEditingRoomType(null);
    setShowForm(false);
  };

  if (loading) {
    return <div className="loading">Loading room types...</div>;
  }

  return (
    <div className="room-type-management">
      {/* Notification */}
      {notification && (
        <div className={`notification ${notification.type}`}>
          {notification.message}
        </div>
      )}

      <div className="room-type-management-header">
        <h2>Room Type Management</h2>
        <button 
          className="btn btn-primary"
          onClick={() => setShowForm(true)}
        >
          Add New Room Type
        </button>
      </div>

      {showForm && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>{editingRoomType ? 'Edit Room Type' : 'Add New Room Type'}</h3>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Name:</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                />
              </div>
              
              <div className="form-group">
                <label>Description:</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows="3"
                  required
                />
              </div>
              
              <div className="form-group">
                <label>Max Capacity:</label>
                <input
                  type="number"
                  value={formData.max_capacity}
                  onChange={(e) => setFormData({...formData, max_capacity: e.target.value})}
                  min="1"
                  required
                />
              </div>
              
              <div className="form-group">
                <label>Price per Night (Php):</label>
                <input
                  type="number"
                  value={formData.price_per_night}
                  onChange={(e) => setFormData({...formData, price_per_night: e.target.value})}
                  min="0"
                  step="0.01"
                  required
                />
              </div>
              
              <div className="form-actions">
                <button type="submit" className="btn btn-primary">
                  {editingRoomType ? 'Update' : 'Create'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={resetForm}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteDialog && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Confirm Delete</h3>
            <p>Are you sure you want to delete room type <strong>"{itemToDelete?.name}"</strong>?</p>
            <p>This action cannot be undone.</p>
            <div className="form-actions">
              <button 
                className="btn btn-danger"
                onClick={confirmDelete}
              >
                Delete Room Type
              </button>
              <button 
                className="btn btn-secondary" 
                onClick={cancelDelete}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="room-types-table">
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Description</th>
              <th>Max Capacity</th>
              <th>Price per Night</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {roomTypes.map(roomType => (
              <tr key={roomType.id}>
                <td>{roomType.name}</td>
                <td>
                  <div style={{
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    lineHeight: '1.4',
                    maxHeight: '4.2em', // 3 lines * 1.4 line-height
                    wordBreak: 'break-word'
                  }}>
                    {roomType.description}
                  </div>
                </td>
                <td>{roomType.max_capacity}</td>
                <td>Php{roomType.price_per_night}</td>
                <td>
                  <button 
                    className="btn btn-small btn-secondary"
                    onClick={() => handleEdit(roomType)}
                  >
                    Edit
                  </button>
                  <button 
                    className="btn btn-small btn-danger"
                    onClick={() => handleDelete(roomType)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default RoomTypeManagement;
